/**
 * MySkillsPage: the signed-in user's own plugin records (epic #514 / Work Item C).
 *
 * Backed by the authenticated `GET /skill/my-records` endpoint (Contract 5), NOT
 * marketplace.json: that catalog is active-only and carries no `status` or
 * `refusalReason`, both of which this page must surface. Each row is a
 * `MyRecordEntry` (deliberately NOT a Contract-3 `MarketplaceEntry`), so we do
 * not reuse PluginCard/PluginListView here; a refused/sparse row lacks the rich
 * fields those read (pluginName/description/categories/etc.) and has nowhere to
 * show status. The row is rendered natively, its display branching on `status`.
 *
 * Records are grouped by `ownership`:
 *   - "Published by you"  — verified-contributor rows (web-uploaded). Edit + Delete.
 *   - "Owned via GitHub"  — self-asserted rows (author-email match). Read-only in v1.
 *   - "Possibly yours"    — tier-2 GitHub-identity matches. Always empty in v1.
 */

import { useCallback, useEffect, useState } from "react";
import type { MyRecordEntry } from "@skills-marketplace/contracts/contract-5-github-proxy";
import {
  getMyRecords,
  deleteSkill,
  GitHubProxyError,
  NetworkBlockedError,
} from "../lib/github-proxy-client";
import { reportError } from "../lib/newrelic";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";
import SourceBadge from "../components/SourceBadge";
import Loading from "../components/Loading";
import InfoIcon from "../icons/InfoIcon";
import LockIcon from "../icons/LockIcon";
import styles from "./MySkillsPage.module.css";

interface Props {
  /**
   * Open the catalog detail page for a record. Only ever called with a present
   * `installName` (a deep-linkable, active+listed row), so the caller can resolve
   * it against the loaded marketplace.json.
   */
  onOpenDetail: (installName: string) => void;
  /**
   * Open the edit page for an editable record. Called with the record's
   * `installName` (the key the edit route resolves against the catalog).
   */
  onEdit: (installName: string) => void;
  /**
   * A record was deleted. Called with its `installName` so the caller can drop
   * it from the catalog context (`removePlugin`). Only fired when `installName`
   * is present (an inactive row isn't in the catalog to begin with).
   */
  onRecordDeleted: (installName: string) => void;
  /** Navigate to the contribute page (whole-page empty-state CTA). */
  onContribute: () => void;
  /** Navigate to the help page (rejection-box eligibility link). */
  onHelp: () => void;
}

// An active record can flip to inactive with no refusalReason via a repo
// lifecycle event or a removed manifest. There is no stored per-record cause, so
// the info tip lists the possibilities rather than asserting one.
const DELIST_REASONS =
  "This usually means the repository was archived, deleted, renamed, or made private, or the plugin manifest was removed from it.";

function formatUpdated(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function skillCount(n: number): string {
  return `${n} skill${n === 1 ? "" : "s"}`;
}

/**
 * A 200 whose body isn't the expected shape. A distinct type (not a plain Error)
 * so messageForError can surface its curated message while keeping raw lower-layer
 * strings (e.g. an MSAL diagnostic) out of the UI.
 */
class MalformedResponseError extends Error {}

/**
 * Map a fetch failure to user-facing copy. Never surface a raw lower-layer message
 * (handleResponse's "non-JSON response (401)", an MSAL AADSTS string): a 401/403 is
 * an auth problem the user must re-authenticate for, a NetworkBlockedError is a
 * connectivity/security-filter problem, and everything else is generic. The raw
 * error still goes to New Relic via reportError for diagnosis.
 */
function messageForError(err: unknown): string {
  if (err instanceof NetworkBlockedError) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  if (err instanceof GitHubProxyError && (err.statusCode === 401 || err.statusCode === 403)) {
    return "Your session may have expired, or you don't have access to this. Try signing in again, or contact IT Support if this keeps happening.";
  }
  if (err instanceof MalformedResponseError) {
    return err.message;
  }
  return "Something went wrong loading your skills. Please try again.";
}

interface MyRecordRowProps {
  entry: MyRecordEntry;
  onOpenDetail: Props["onOpenDetail"];
  onHelp: Props["onHelp"];
  /**
   * True only in the "Published by you" section: renders Edit (when deep-linkable)
   * and Delete controls. Requires onEdit + onDeleted.
   */
  canManage?: boolean;
  onEdit?: Props["onEdit"];
  onDeleted?: (entry: MyRecordEntry) => void;
  /** True for GitHub-sourced (read-only) rows: renders the lock note instead of controls. */
  readOnly?: boolean;
}

function MyRecordRow({ entry, onOpenDetail, onHelp, canManage, onEdit, onDeleted, readOnly }: MyRecordRowProps) {
  const { installKey, installName, displayName, orgOrOwner, repoName, status, hidden, refusalReason, updatedAt, ownership } = entry;
  const published = status === "active" && !hidden;
  const source = ownership === "verified-contributor" ? "web-upload" : "github";

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleDelete() {
    // Mirrors PluginDetailPage.handleDelete: native confirm, then a proxy DELETE.
    if (!window.confirm(`Delete "${displayName}"? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteSkill(installKey);
      // Parent removes this row from state (unmounting it) and drops it from the
      // catalog context; no post-success state set is needed here.
      onDeleted?.(entry);
    } catch (err) {
      reportError(err, "my-records-delete");
      setDeleting(false);
      setDeleteError("We couldn't delete this skill. Please try again.");
    }
  }

  return (
    <li className={styles.row}>
      {installName ? (
        <button type="button" className={styles.titleLink} onClick={() => onOpenDetail(installName)}>
          {displayName}
        </button>
      ) : (
        <span className={styles.title}>{displayName}</span>
      )}
      <div className={styles.badges}>
        <SourceBadge source={source} />
        {published ? (
          <StatusBadge tone="success">Listed</StatusBadge>
        ) : status === "active" ? (
          <StatusBadge tone="warning">Hidden</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">Not listed</StatusBadge>
        )}
      </div>

      <p className={styles.meta}>
        {orgOrOwner}/{repoName} · Updated {formatUpdated(updatedAt)}
      </p>

      {/*
        Status line, driven by status. A published (active + visible) row gets no
        line: the "Listed" badge says it all. A hidden row needs a visible,
        actionable line (not a mouse-only tooltip) because the exclude topic is the
        one thing its owner can act on to relist it.
      */}
      {status === "active" && hidden && (
        <p className={styles.hiddenNote}>
          Hidden from the catalog. Remove the <code>plugin-marketplace-exclude</code> repository
          topic to list it again.
        </p>
      )}
      {status === "inactive" &&
        (refusalReason ? (
          <div className={styles.reasonBox}>
            <span className={styles.reasonLabel}>Rejection reason</span>
            {/* Author-controlled (contract-5): render as escaped text, never markup. */}
            <p className={styles.reasonText}>{refusalReason}</p>
            <button type="button" className={styles.reasonHelp} onClick={onHelp}>
              What makes a skill eligible?
            </button>
          </div>
        ) : (
          <p className={styles.noLongerListed}>
            <span>No longer listed.</span>
            <InfoIcon className={styles.infoIcon} label={DELIST_REASONS} />
          </p>
        ))}

      {canManage && (
        <div className={styles.actions}>
          {installName && (
            <button type="button" className={styles.editButton} onClick={() => onEdit?.(installName)}>
              Edit
            </button>
          )}
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
          {deleteError && (
            <p className={styles.deleteError} role="alert">
              {deleteError}
            </p>
          )}
        </div>
      )}

      {readOnly && (
        <p className={styles.lockNote}>
          <LockIcon className={styles.lockIcon} />
          Read-only. Edit and delete aren&apos;t available for GitHub-sourced skills in v1.
        </p>
      )}
    </li>
  );
}

export default function MySkillsPage({ onOpenDetail, onEdit, onRecordDeleted, onContribute, onHelp }: Props) {
  const [owned, setOwned] = useState<MyRecordEntry[]>([]);
  const [possiblyYours, setPossiblyYours] = useState<MyRecordEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  const handleDeleted = useCallback(
    (entry: MyRecordEntry) => {
      setOwned((prev) => prev.filter((e) => e.installKey !== entry.installKey));
      // Only listed rows are in the catalog context; an inactive/refused row has
      // no installName and nothing to remove there.
      if (entry.installName) onRecordDeleted(entry.installName);
    },
    [onRecordDeleted],
  );

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    getMyRecords()
      .then((res) => {
        if (cancelled) return;
        // handleResponse only casts the parsed JSON to the response type; it
        // does not validate the shape. A 200 with an unexpected body would set
        // state to undefined and then throw in render, OUTSIDE this promise, so
        // it would escape the catch below and trip the app ErrorBoundary instead
        // of this page's own error/Retry. Treat a malformed success as an error
        // so the user gets Retry, not a misleading "no skills" empty state.
        if (!Array.isArray(res?.owned) || !Array.isArray(res?.possiblyYours)) {
          throw new MalformedResponseError("The server returned an unexpected response.");
        }
        setOwned(res.owned);
        setPossiblyYours(res.possiblyYours);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Keep the status on the telemetry so on-call can separate auth (401/403)
        // from backend 5xx from a network block, then show curated copy rather than
        // the raw lower-layer message.
        const status = err instanceof GitHubProxyError ? err.statusCode : undefined;
        if (status === undefined) {
          reportError(err, "my-records-fetch");
        } else {
          reportError(err, "my-records-fetch", { status });
        }
        setError(messageForError(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (isLoading) {
    return <Loading message="Loading your skills..." />;
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <h1>Couldn&apos;t load your skills</h1>
        <p className={styles.errorMessage}>{error}</p>
        <button type="button" onClick={retry} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  const published = owned.filter((e) => e.ownership === "verified-contributor");
  const gitHubOwned = owned.filter((e) => e.ownership === "self-asserted");
  const isEmpty = owned.length === 0 && possiblyYours.length === 0;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>My Skills</h1>
        <p className={styles.subtitle}>
          Skills and plug-ins matched to your account, including ones that are no longer listed.
          Rejection reasons are shown only to you.
        </p>
      </div>

      {isEmpty ? (
        <EmptyState message="You haven't published any skills yet.">
          <p className={styles.emptyHint}>
            When you publish a skill or plug-in, by web upload or from a GitHub repo, it&apos;ll
            show up here with its status and any feedback.
          </p>
          <button type="button" className={styles.ctaButton} onClick={onContribute}>
            Publish your first skill
          </button>
        </EmptyState>
      ) : (
        <>
          <section aria-labelledby="published-heading" className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 id="published-heading" className={styles.sectionTitle}>Published by you</h2>
              <span className={styles.sectionCount}>{skillCount(published.length)}</span>
            </div>
            <p className={styles.sectionSub}>
              Skills you uploaded through the marketplace. You can edit or delete them.
            </p>
            {published.length === 0 ? (
              <p className={styles.sectionEmpty}>
                You haven&apos;t published any skills through the marketplace yet.
              </p>
            ) : (
              <ul className={styles.list}>
                {published.map((entry) => (
                  <MyRecordRow
                    key={entry.installKey}
                    entry={entry}
                    canManage
                    onOpenDetail={onOpenDetail}
                    onEdit={onEdit}
                    onDeleted={handleDeleted}
                    onHelp={onHelp}
                  />
                ))}
              </ul>
            )}
          </section>

          {gitHubOwned.length > 0 && (
            <section aria-labelledby="github-heading" className={styles.section}>
              <div className={styles.sectionHead}>
                <h2 id="github-heading" className={styles.sectionTitle}>Owned via GitHub</h2>
                <span className={styles.sectionCount}>{skillCount(gitHubOwned.length)}</span>
              </div>
              <p className={styles.sectionSub}>
                Skills from GitHub repos whose manifest author email matches yours. Status and
                any rejection reason are visible, but edit and delete stay web-upload-only in v1.
              </p>
              <ul className={styles.list}>
                {gitHubOwned.map((entry) => (
                  <MyRecordRow
                    key={entry.installKey}
                    entry={entry}
                    readOnly
                    onOpenDetail={onOpenDetail}
                    onHelp={onHelp}
                  />
                ))}
              </ul>
            </section>
          )}

          <section aria-labelledby="possibly-heading" className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 id="possibly-heading" className={styles.sectionTitle}>Possibly yours</h2>
              <span className={styles.sectionCount}>{skillCount(possiblyYours.length)}</span>
            </div>
            <p className={styles.sectionSub}>
              Records that may belong to you based on your GitHub identity. Matching by GitHub
              login isn&apos;t available yet
              {possiblyYours.length === 0 ? ", so this list is empty for now." : "."}
            </p>
            {possiblyYours.length === 0 ? (
              <p className={styles.sectionEmpty}>Nothing here yet.</p>
            ) : (
              <ul className={styles.list}>
                {possiblyYours.map((entry) => (
                  <MyRecordRow
                    key={entry.installKey}
                    entry={entry}
                    readOnly
                    onOpenDetail={onOpenDetail}
                    onHelp={onHelp}
                  />
                ))}
              </ul>
            )}
          </section>

          <p className={styles.tierNote}>
            GitHub skills that match neither your manifest author email nor your GitHub push
            identity don&apos;t appear here.
          </p>
        </>
      )}
    </div>
  );
}
